import React, { useEffect, useState } from 'react';
import { Modal } from 'components/modal/Modal';
import Button from 'components/button/Button';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { CheckboxInput } from 'components/form/CheckboxInput';
import styles from './ESignModal.module.scss';
import PDFViewer from 'components/pdf/PDFViewer';
import { useSelector } from 'react-redux';
import { selectUserInfo } from 'reducers/componentResources';
type Props = {
    address?: string;
    close(): void;
    programType?: string;
    onConcentAccepted: () => void;
    product?: string;
};
const validationSchema = Yup.object({
    acceptConsent: Yup.boolean()
        .oneOf([true], 'You must accept the E-Sign Consent to proceed.')
        .required('Required'),
    acceptCHA: Yup.boolean()
        .oneOf([true], 'You must accept the Cardholder Agreement to proceed.')
        .required('Required'),
});
function getHtmFile(programType: string | undefined): string {
    if (programType === 'CorporateCommercialPrepaid') return '/e-sign/E-Sign_Commercial_PDF.pdf';
    else if (programType === 'InstantIssuePrepaid') return '/e-sign/E-Sign_Instant_Issue_PDF.pdf';
    return '/e-sign/E-Sign_Reloadable_PDF.pdf';
}
function ESignModal(props: Props) {
    const [isScrolledToEnd, setIsScrolledToEnd] = useState(false);
    const [isConcentAccepted, setIsConcentAccepted] = useState(false);
    const [isAgreementAccepted, setIsAgreementAccepted] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const userInfo = useSelector(selectUserInfo);
    const initialValues = {
        acceptConsent: false,
        acceptCHA: false,
    };
    const handleAcceptESign = () => {
        setIsConcentAccepted((prevValue) => !prevValue);
    };
    const handleAgreementChange = () => {
        setIsAgreementAccepted((prevValue) => !prevValue);
    };

    type EligibilityResult = {
        crb_commercial: string | null;
        crb_consumer: string | null;
        girasol: string | null;
    };

    // DOC Paths
    const GIRASOL_DOC = "/e-sign/GIRASOL PAYMENT SOLUTIONS DEBIT MASTERCARD CARDHOLDER AGREEMENT (1).docx.pdf";
    const CRB_COMMERCIAL_DOC = "/e-sign/Commercial_Deposit_Account_Agreement.pdf";
    const CRB_CONSUMER_DOC = "/e-sign/Consumer_Agreement_BaaS.pdf";

    const checkEligibility = (
        type?: string,
        product?: string
    ): EligibilityResult => {
        if (!type || !product) {
            return {
                crb_commercial: null,
                crb_consumer: null,
                girasol: null
            };
        }

        const productNormalized = product?.replace(/\s+/g, '')?.toLowerCase();
        const isCrossRiverBank = productNormalized === 'crossriverbank' || product === 'Cross River Bank';

        switch (true) {
            case isCrossRiverBank:
                if (type === 'Business') {
                    return {
                        crb_commercial: CRB_COMMERCIAL_DOC,
                        crb_consumer: null,
                        girasol: null
                    };
                }

                if (type === 'Personal') {
                    return {
                        crb_commercial: null,
                        crb_consumer: CRB_CONSUMER_DOC,
                        girasol: null
                    };
                }
                break;

            case productNormalized === 'girasol':
                return {
                    crb_commercial: null,
                    crb_consumer: null,
                    girasol: GIRASOL_DOC
                };
        }

        // Explicit fallback
        return {
            crb_commercial: null,
            crb_consumer: null,
            girasol: null
        };
    };

    const finalAgreementFile = checkEligibility(userInfo?.accountType, props.product);
    const renderPDFs = () => {
        return finalAgreementFile && Object.entries(finalAgreementFile).map(([key, value]) => {
            return (
                value && (
                    <PDFViewer key={key} fileUrl={value} setIsScrolledToEnd={setIsScrolledToEnd} />
                )
            );
        });
    };

    return (
        <Modal
            title="Cardholder Agreement"
            onCloseModal={props.close}
            className={styles.modal_content}
            customButtons={
                <div className="ModalNavigation">
                    <Button priority="secondary" type="button" onClick={props.close}>
                        Cancel
                    </Button>
                    <Button
                        priority="primary"
                        type="submit"
                        disabled={!isConcentAccepted || !isAgreementAccepted}
                        className="Btn BtnRed"
                        onClick={props.onConcentAccepted}
                    >
                        Order Card
                    </Button>
                </div>
            }
        >
            <Formik
                initialValues={initialValues}
                // validationSchema={validationSchema}
                onSubmit={(values) => { }}
            >
                {({ errors, touched, isValid, values }) => (
                    <Form>
                        {renderPDFs()}
                        <PDFViewer
                            fileUrl={"/e-sign/E-Sign_Disclosure_and_Consent_Agreement.pdf"}
                            setIsScrolledToEnd={setIsScrolledToEnd}
                        />
                        <br />
                        <div>
                            <CheckboxInput
                                disabled={!isScrolledToEnd}
                                name="acceptConsent"
                                label={`I accept the E-Sign Consent`}
                                onChange={handleAcceptESign}
                                checked={isConcentAccepted}
                            />
                            {errors.acceptConsent && touched.acceptConsent && (
                                <div style={{ color: 'red' }}>{errors.acceptConsent}</div>
                            )}
                        </div>
                        <br />
                        <div>
                            <CheckboxInput
                                disabled={!isScrolledToEnd}
                                name="acceptCHA"
                                label={`I accept the Cardholder Agreement`}
                                onChange={handleAgreementChange}
                                checked={isAgreementAccepted}
                            />
                            {errors.acceptCHA && touched.acceptCHA && (
                                <div style={{ color: 'red' }}>{errors.acceptCHA}</div>
                            )}
                        </div>
                        <p style={{ color: 'red', fontSize: 12, minHeight: 20 }}>
                            {!isScrolledToEnd ? (
                                'Please read through the entire consent text to continue.'
                            ) : (
                                <></>
                            )}
                            <br />
                            {errorMessage && errorMessage}
                        </p>
                    </Form>
                )}
            </Formik>
        </Modal>
    );
}
export default ESignModal;
